import React, { useState } from 'react';
import { Star, ChevronLeft, ChevronRight, Quote, CheckCircle, Filter } from 'lucide-react';
import { Button } from './ui/button';
import { testimonials } from '../data/mock';

const TestimonialsSection = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [filter, setFilter] = useState('all');
  const testimonialsPerPage = 8;

  const filteredTestimonials = testimonials.filter(t => {
    if (filter === '5stars') return t.stars === 5;
    if (filter === '4stars') return t.stars === 4;
    return true;
  });

  const totalPages = Math.ceil(filteredTestimonials.length / testimonialsPerPage);
  const startIndex = (currentPage - 1) * testimonialsPerPage;
  const currentTestimonials = filteredTestimonials.slice(startIndex, startIndex + testimonialsPerPage);

  const stats = {
    total: testimonials.length,
    fiveStars: testimonials.filter(t => t.stars === 5).length,
    fourStars: testimonials.filter(t => t.stars === 4).length,
    avgRating: (testimonials.reduce((acc, t) => acc + t.stars, 0) / testimonials.length).toFixed(1)
  };

  return (
    <section id="temoignages" className="py-16 sm:py-28 bg-gradient-to-b from-black via-gray-950 to-black">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-10 sm:mb-16">
          <span className="inline-block px-4 sm:px-5 py-2 bg-amber-500/10 border border-amber-500/30 rounded-full text-amber-500 text-xs sm:text-sm font-semibold tracking-wide mb-4 sm:mb-6">
            Témoignages Vérifiés
          </span>
          <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 sm:mb-8">
            <span className="text-amber-500">{stats.total}</span> Clients Satisfaits
          </h2>
          <p className="text-gray-300 text-base sm:text-xl max-w-3xl mx-auto leading-relaxed px-2">
            Découvrez les témoignages de personnes qui ont retrouvé l'amour grâce au grand marabout africain.
          </p>
        </div>

        {/* Stats Bar */}
        <div className="flex flex-wrap justify-center gap-3 sm:gap-6 mb-8 sm:mb-14">
          <div className="flex items-center space-x-2 sm:space-x-4 bg-gray-900/60 border border-amber-500/20 rounded-full px-4 sm:px-8 py-2 sm:py-4">
            <Star className="w-4 h-4 sm:w-6 sm:h-6 text-amber-500 fill-amber-500" />
            <span className="text-white font-bold text-base sm:text-xl">{stats.avgRating}</span>
            <span className="text-gray-300 text-xs sm:text-base">Note moyenne</span>
          </div>
          <div className="flex items-center space-x-2 sm:space-x-4 bg-gray-900/60 border border-amber-500/20 rounded-full px-4 sm:px-8 py-2 sm:py-4">
            <CheckCircle className="w-4 h-4 sm:w-6 sm:h-6 text-green-500" />
            <span className="text-gray-300 text-xs sm:text-base">100% Vérifiés</span>
          </div>
        </div>

        {/* Filter Buttons */}
        <div className="flex flex-wrap justify-center gap-2 sm:gap-4 mb-8 sm:mb-12">
          <Button
            variant={filter === 'all' ? 'default' : 'outline'}
            onClick={() => { setFilter('all'); setCurrentPage(1); }}
            className={`text-xs sm:text-base px-3 sm:px-6 py-2 sm:py-3 ${filter === 'all' ? 'bg-amber-500 text-black hover:bg-amber-400' : 'border-amber-500/30 text-gray-300 hover:bg-amber-500/10'}`}
          >
            <Filter className="w-3 h-3 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
            Tous ({stats.total})
          </Button>
          <Button
            variant={filter === '5stars' ? 'default' : 'outline'}
            onClick={() => { setFilter('5stars'); setCurrentPage(1); }}
            className={`text-xs sm:text-base px-3 sm:px-6 py-2 sm:py-3 ${filter === '5stars' ? 'bg-amber-500 text-black hover:bg-amber-400' : 'border-amber-500/30 text-gray-300 hover:bg-amber-500/10'}`}
          >
            <Star className="w-3 h-3 sm:w-5 sm:h-5 mr-1 sm:mr-2 fill-current" />
            5 étoiles
          </Button>
          <Button
            variant={filter === '4stars' ? 'default' : 'outline'}
            onClick={() => { setFilter('4stars'); setCurrentPage(1); }}
            className={`text-xs sm:text-base px-3 sm:px-6 py-2 sm:py-3 ${filter === '4stars' ? 'bg-amber-500 text-black hover:bg-amber-400' : 'border-amber-500/30 text-gray-300 hover:bg-amber-500/10'}`}
          >
            <Star className="w-3 h-3 sm:w-5 sm:h-5 mr-1 sm:mr-2" />
            4 étoiles
          </Button>
        </div>

        {/* Testimonials Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 sm:gap-6">
          {currentTestimonials.map((testimonial) => (
            <div
              key={testimonial.id}
              className="bg-gray-900/50 border border-amber-500/10 rounded-xl sm:rounded-2xl p-5 sm:p-7 hover:border-amber-500/30 transition-all duration-300"
            >
              {/* Quote Icon */}
              <Quote className="w-8 h-8 sm:w-10 sm:h-10 text-amber-500/30 mb-3 sm:mb-5" />
              
              {/* Stars */}
              <div className="flex mb-3 sm:mb-4">
                {[...Array(5)].map((_, i) => (
                  <Star
                    key={i}
                    className={`w-4 h-4 sm:w-5 sm:h-5 ${i < testimonial.stars ? 'text-amber-500 fill-amber-500' : 'text-gray-600'}`}
                  />
                ))}
              </div>

              {/* Text */}
              <p className="text-gray-200 text-sm sm:text-base mb-4 sm:mb-5 line-clamp-4 leading-relaxed">
                "{testimonial.text}"
              </p>

              {/* Author */}
              <div className="flex items-center justify-between pt-4 sm:pt-5 border-t border-amber-500/10">
                <div>
                  <div className="flex items-center space-x-2">
                    <span className="text-white font-semibold text-sm sm:text-base">{testimonial.name}</span>
                    {testimonial.verified && (
                      <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-green-500" />
                    )}
                  </div>
                  <span className="text-gray-400 text-xs sm:text-sm">{testimonial.city}</span>
                </div>
                <span className="text-gray-500 text-xs">{testimonial.date}</span>
              </div>
            </div>
          ))}
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-center space-x-2 sm:space-x-4 mt-10 sm:mt-14">
          <Button
            variant="outline"
            onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
            disabled={currentPage === 1}
            className="border-amber-500/30 text-amber-500 hover:bg-amber-500/10 disabled:opacity-50 h-10 w-10 sm:h-12 sm:w-12 p-0"
          >
            <ChevronLeft className="w-5 h-5 sm:w-6 sm:h-6" />
          </Button>
          
          <div className="flex items-center space-x-1 sm:space-x-2">
            {[...Array(Math.min(3, totalPages))].map((_, i) => {
              let pageNum;
              if (totalPages <= 3) {
                pageNum = i + 1;
              } else if (currentPage <= 2) {
                pageNum = i + 1;
              } else if (currentPage >= totalPages - 1) {
                pageNum = totalPages - 2 + i;
              } else {
                pageNum = currentPage - 1 + i;
              }
              
              return (
                <button
                  key={i}
                  onClick={() => setCurrentPage(pageNum)}
                  className={`w-9 h-9 sm:w-12 sm:h-12 rounded-full text-sm sm:text-base font-semibold transition-all duration-300 ${
                    currentPage === pageNum
                      ? 'bg-amber-500 text-black'
                      : 'bg-gray-800 text-gray-300 hover:bg-gray-700'
                  }`}
                >
                  {pageNum}
                </button>
              );
            })}
          </div>

          <Button
            variant="outline"
            onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
            disabled={currentPage === totalPages}
            className="border-amber-500/30 text-amber-500 hover:bg-amber-500/10 disabled:opacity-50 h-10 w-10 sm:h-12 sm:w-12 p-0"
          >
            <ChevronRight className="w-5 h-5 sm:w-6 sm:h-6" />
          </Button>
        </div>

        {/* Page Info */}
        <p className="text-center text-gray-400 text-sm sm:text-base mt-4 sm:mt-6">
          Page {currentPage} sur {totalPages}
        </p>
      </div>
    </section>
  );
};

export default TestimonialsSection;
